import { useComposerMultilinePrompt } from "./useComposerMultilinePrompt";
import { useThreadComposerLayout } from "./threadComposerLayoutContext";
import { useLayoutEffect, useState, type RefObject } from "react";

import { useRestingComposerControlsLayout } from "./useRestingComposerControlsLayout";
import {
  useRestingComposerDecision,
  type RestingComposerDecisionInput,
} from "./useRestingComposerDecision";
import { useRestingComposerTransition } from "./useRestingComposerTransition";

export interface RestingComposerState {
  /** True while the Composer shows its single-line layout. */
  readonly isResting: boolean;
  /** True below `md`, so the footer can own the controls the strip hides there. */
  readonly isMobileViewport: boolean;
  /** Handed to the controls strip so the portal knows where to land. */
  readonly controlsHostRef: (element: HTMLDivElement | null) => void;
  readonly controlsHost: HTMLDivElement | null;
  readonly controlsRef: RefObject<HTMLDivElement | null>;
  readonly controlsVisible: boolean;
  /** Called by the chat view when the timeline arrives back at its newest turn. */
  readonly restoreAfterTimelineReachedEnd: () => void;
}

/**
 * Everything the Resting Composer needs, behind one call.
 *
 * `ChatComposer` is at its file-size cap, so the pieces are composed here
 * rather than there: the decision and its triggers, the portal host, whether
 * the relocated controls fit it, and the transition between the two layouts.
 * The orchestrator sees one boolean and one host.
 */
export function useRestingComposer(
  input: RestingComposerDecisionInput & {
    readonly formRef: RefObject<HTMLFormElement | null>;
  },
): RestingComposerState {
  const hasMultilinePrompt = useComposerMultilinePrompt(input.formRef);
  const { isResting, isMobileViewport, restoreAfterTimelineReachedEnd } =
    useRestingComposerDecision({ ...input, hasMultilinePrompt });
  const { setResting } = useThreadComposerLayout();
  useLayoutEffect(() => {
    setResting(isResting && !isMobileViewport);
  }, [isResting, isMobileViewport, setResting]);
  const [controlsHost, setControlsHost] = useState<HTMLDivElement | null>(null);
  const { controlsRef, visible } = useRestingComposerControlsLayout(
    isResting ? controlsHost : null,
  );
  useRestingComposerTransition({ formRef: input.formRef, controlsRef, isResting });

  return {
    isResting,
    isMobileViewport,
    controlsHostRef: setControlsHost,
    controlsHost,
    controlsRef,
    controlsVisible: visible,
    restoreAfterTimelineReachedEnd,
  };
}
